<template>
    <div v-if="!isPurchaseLimitReached">
        <div v-if="loading"></div>
        <div v-else-if="error"></div>
        <div v-if="!loading && !error && usedLayout" class="po-canvas-host" ref="canvasHost" :style="canvasHostStyle">
        <div class="po-canvas" :style="canvasStyle">

    <div class="canvas-items">

        <div
            v-for="(item,index) in items"
            :key="item.id"
            :class="[
                'canvas-item',
                item.type,
                item.pinGroupId ? 'pinned' : '',
                isBuyItem(item) ? 'is-buy-item' : ''
            ]"
            :data-item-id="item.id"
            :style="itemStyle(item,index)"
            @click.stop="onItemClick(item)"
        >

            <img
                v-if="item.type==='asset'"
                class="asset-image"
                :src="item.data.imagePath"
                :alt="item.data.name"
            >

            <span
                v-else
                class="item-label"
                :style="textStyle(item)"
            >{{ labelText(item) }}</span>

        </div>

    </div>

</div>
</div>
    </div>
</template>

<script>
    import { createRenderer } from '../../utils/poRenderer.js';
    import utils from '../../utils/utils.js';
    export default {
        name: 'DynamicPoTemplate',
        props: {
            data: Object,
            extra: { type: Object, default: () => ({}) },
            poIndex: { type: Number, default: -1 },
            regularReward: { type: String, default: 'NOT_CHECKED' },
            layout: { type: Object, default: null },
            layoutUrl: { type: String, default: '' },
            locale: { type: String, default: (typeof window !== 'undefined' && window._i18n && window._i18n.locale) || 'en' }
        },
        data() {
            return {
                loading: true,
                error: null,
                fetchedLayout: null,
                resolvedLayout: null,
                cooldown: 0,
                productsState: [],
                purchaseLimitState: 0,
                regularRewardTimer: null,
                availableWidth: 0,
                resizeObserver: null,
                textLengthData: {
                    coins_1_value: null,
                    chips_1_value: null
                },
                lengthAnalyzed: false,
                textLengthDifference: 0,
                loadPurchaseChecked: false
            };
        },
        computed: {
            usedLayout() { return this.resolvedLayout || this.layout || this.fetchedLayout || null; },
            designWidth() {
                const cfg = this.usedLayout && this.usedLayout.config;
                return (cfg && cfg.canvasWidth) || 1080;
            },
            designHeight() {
                const cfg = this.usedLayout && this.usedLayout.config;
                return (cfg && cfg.canvasHeight) || 868;
            },
            items() {
                const rawItems = (this.usedLayout && this.usedLayout.layout && this.usedLayout.layout.droppedItems) || [];
                const visibleItems = rawItems.filter((item) => this.renderer.isItemVisible(item));
                return visibleItems.length ? visibleItems : rawItems;
            },
            backgroundSrc() {
                const items = this.items;
                const bg = items.find(it => it.isBackgroundLayer || (it.type === 'asset' && it.data && /background/i.test(it.data.name)));
                return bg && bg.data && bg.data.imagePath ? bg.data.imagePath : null;
            },
            isMobileCanvas() {
                if (typeof window === 'undefined') return false;
                if (window._global && typeof window._global.isMobile !== 'undefined') return !!window._global.isMobile;
                return window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
            },
            canvasScale() {
                const width = this.availableWidth || this.designWidth;
                const ratio = width / this.designWidth;
                return this.isMobileCanvas ? Math.min(ratio, 1) : Math.min(ratio, 1.35);
            },
            canvasHostStyle() {
                return {
                    position: 'relative',
                    width: '100%',
                    maxWidth: this.isMobileCanvas ? (this.designWidth + 'px') : 'none',
                    height: Math.round(this.designHeight * this.canvasScale) + 'px',
                    overflow: 'hidden'
                };
            },
            canvasStyle() {
                return {
                    position: 'relative',
                    width: this.designWidth + 'px',
                    height: this.designHeight + 'px',
                    overflow: 'hidden',
                    transform: `scale(${this.canvasScale})`,
                    transformOrigin: 'top left'
                };
            },
            renderer() {
                const cfg = (this.usedLayout && this.usedLayout.config) || {};
                return createRenderer({
                    designWidth: cfg.canvasWidth || 1080,
                    designHeight: cfg.canvasHeight || 868,
                    actualWidth: cfg.canvasWidth,
                    actualHeight: cfg.canvasHeight,
                    activeState: cfg.activeState || 'default'
                })
            },
            // only rolling PO tracks a purchase limit; other types never set data.purchaseLimit
            isPurchaseLimitReached() {
                return !!(this.data && this.data.type === 'web_rolling_po' && this.hasValue(this.data.purchaseLimit) && this.purchaseLimitState === 0);
            }
        },
        async mounted() {
            this.$nextTick(() => {
                this.setupCanvasResize();
                this.updateCanvasBounds();
            });

            if (this.layout) {
                // ensure fonts from provided layout are loaded
                this.loadUploadedFonts(this.usedLayout);
                this.loading = false;
                if (this.regularReward === 'COLLECTED') this.$nextTick(() => this.buyFreeProductOnLoad());
                return;
            }
            if (!this.layoutUrl) {
                this.error = 'layoutUrl is empty';
                this.loading = false;
                return;
            }
            try {
                const res = await fetch(this.layoutUrl);
                if (!res.ok) throw new Error('status ' + res.status);
                const text = await res.text();
                const trimmed = (text || '').trim();
                if (!trimmed || trimmed === 'undefined') {
                    this.error = 'layout empty or undefined';
                } else {
                    try {
                        this.fetchedLayout = JSON.parse(text);
                        this.refreshResolvedLayout();
                    } catch (parseErr) {
                        console.warn('DynamicPoTemplate: invalid JSON from layoutUrl', parseErr, text);
                        this.error = 'invalid JSON from layoutUrl';
                    }
                }
            } catch (e) {
                this.error = e.message || String(e);
            } finally {
                this.loading = false;
                if (this.regularReward === 'COLLECTED') this.$nextTick(() => this.buyFreeProductOnLoad());
            }
        },
        watch: {
            layout: {
                handler() {
                    this.refreshResolvedLayout();
                },
                deep: true
            },
            fetchedLayout: {
                handler() {
                    this.refreshResolvedLayout();
                },
                deep: true
            },
            locale() {
                this.refreshResolvedLayout();
            },
            data: {
                handler() {
                    this.syncProductsState();
                    this.refreshResolvedLayout();
                },
                deep: true
            },
            regularReward(newValue) {
                this.handleRegularReward(newValue);
            },
            usedLayout(newLayout) {
                if (newLayout) this.loadUploadedFonts(newLayout);
                this.$nextTick(() => this.updateCanvasBounds());
            }
        },
        beforeDestroy() {
            this.clearRegularRewardTimer();
            this.teardownCanvasResize();
        },
        created() {
            this.syncProductsState();
            this.handleRegularReward(this.regularReward);
        },
        methods: {
            normalizeImagePath(path) {
                if (typeof path !== 'string') return path;

                const trimmed = path.trim();
                if (!trimmed) return trimmed;
                if (/^\/\//.test(trimmed)) return trimmed;
                if (/^https?:\/\//i.test(trimmed)) return trimmed;

                if (/^s3\.amazonaws\.com\//i.test(trimmed)) {
                    return `//${trimmed}`;
                }

                return trimmed;
            },
            normalizeDroppedItemsImagePaths(items) {
                if (!Array.isArray(items)) return;

                items.forEach((item) => {
                    if (!item || !item.data || !item.data.imagePath) return;

                    item.data.imagePath = this.normalizeImagePath(item.data.imagePath);
                });
            },
            setupCanvasResize() {
                if (typeof window !== 'undefined') {
                    window.addEventListener('resize', this.updateCanvasBounds);
                }

                if (typeof ResizeObserver === 'function' && this.$refs.canvasHost) {
                    this.resizeObserver = new ResizeObserver(() => this.updateCanvasBounds());
                    this.resizeObserver.observe(this.$refs.canvasHost);
                }
            },
            teardownCanvasResize() {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', this.updateCanvasBounds);
                }

                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                    this.resizeObserver = null;
                }
            },
            updateCanvasBounds() {
                const host = this.$refs.canvasHost;
                if (!host) return;

                const width = Number(host.clientWidth) || this.designWidth;
                this.availableWidth = width;
            },
            deriveActiveStateFromProducts(states, fallbackState) {
                const stateList = Array.isArray(states) ? states : [];
                const products = this.getProducts();

                if (!stateList.length || !products.length) return fallbackState || 'default';

                let activeIndex = products.findIndex((prod) => prod && prod.purchased === false);
                if (activeIndex < 0) {
                    activeIndex = products.findIndex((prod) => prod && prod.isLocked === false);
                }
                if (activeIndex < 0) return stateList[stateList.length - 1] || fallbackState || 'default';

                return stateList[activeIndex] || fallbackState || stateList[0] || 'default';
            },
            syncProductsState() {
                const products = this.data && Array.isArray(this.data.products) ? this.data.products : [];
                this.productsState = JSON.parse(JSON.stringify(products));
                this.purchaseLimitState = this.data && this.hasValue(this.data.purchaseLimit) ? Number(this.data.purchaseLimit) : 0;
            },
            getProducts() {
                return Array.isArray(this.productsState) ? this.productsState : [];
            },
            hasValue(v) {
                return v !== undefined && v !== null;
            },
            fc(currency, type) {
                const bonus = type === 1
                    ? Number((this.extra && this.extra.coins) || 0)
                    : Number((this.extra && this.extra.chips) || 0);
                return utils.fc(Math.round(Number(currency) * (1 + bonus)));
            },
            formatAmount(v) {
                return this.hasValue(v) ? utils.fc(v) : '';
            },
            stringifyValue(v) {
                return this.hasValue(v) ? String(v) : '';
            },
            getActiveLocale(cfg) {
                const normalized = (this.locale || '').toLowerCase();
                if (normalized) return normalized;

                const fromConfig = cfg && cfg.activeLocale;
                return fromConfig ? String(fromConfig).toLowerCase() : 'en';
            },
            resolveLocaleLayout(localeLayouts, targetLocale, fallbackLocale) {
                if (!localeLayouts || typeof localeLayouts !== 'object') return null;

                if (localeLayouts[targetLocale]) return { locale: targetLocale, layout: localeLayouts[targetLocale] };

                if (targetLocale && targetLocale.includes('-')) {
                    const baseLocale = targetLocale.split('-')[0];
                    if (localeLayouts[baseLocale]) return { locale: baseLocale, layout: localeLayouts[baseLocale] };
                }

                if (fallbackLocale && localeLayouts[fallbackLocale]) return { locale: fallbackLocale, layout: localeLayouts[fallbackLocale] };
                if (localeLayouts.en) return { locale: 'en', layout: localeLayouts.en };

                const firstLocale = Object.keys(localeLayouts)[0];
                return firstLocale ? { locale: firstLocale, layout: localeLayouts[firstLocale] } : null;
            },
            buildProductDynamicVariables() {
                const vars = {};
                const products = this.getProducts();
                /*const exchangeRate = (this.data && this.hasValue(this.data.exchange_rate)) ? Number(this.data.exchange_rate) : 1;*/

                products.forEach((prod, idx) => {
                    const n = idx + 1;
                    const items = (prod && prod.items) || {};

                    const coins = this.hasValue(items.coins) ? items.coins : prod && prod.coins;
                    const chips = this.hasValue(items.chips) ? items.chips : prod && prod.chips;
                    const bp = this.hasValue(prod && prod.bp) ? prod.bp : items.bp;
                    const luckyCoins = this.hasValue(prod && prod.golden_tokens)
                        ? prod.golden_tokens
                        : (this.hasValue(items.golden_token) ? items.golden_token : items.lucky_coins);
                    const buyPrice = prod && prod.price;

                    if (this.hasValue(coins)) {
                        vars[`coins_${n}_value`] = this.fc(coins, 1);
                        vars[`coins_${n}`] = this.fc(coins, 1);
                    }
                    if (this.hasValue(chips)) {
                        vars[`chips_${n}_value`] = this.fc(chips, 2);
                        vars[`chips_${n}`] = this.fc(chips, 2);
                    }
                    if (this.hasValue(bp)) {
                        vars[`bp_${n}_value`] = this.formatAmount(bp);
                        vars[`bp_${n}`] = this.formatAmount(bp);
                    }
                    if (this.hasValue(luckyCoins)) {
                        vars[`lucky_coins_${n}_value`] = this.formatAmount(luckyCoins);
                        vars[`lucky_coins_${n}`] = this.formatAmount(luckyCoins);
                    }
                    if (this.hasValue(buyPrice)) {
                        //vars[`buy_price_${n}`] = this.stringifyValue(buyPrice);
                        vars[`price_${n}_value`] = this.stringifyValue(buyPrice);
                        vars[`buy_price_${n}`] = this.stringifyValue(buyPrice);
                    }
                });

                return vars;
            },
            refreshResolvedLayout() {
                const source = this.layout || this.fetchedLayout;
                if (!source) {
                    this.resolvedLayout = null;
                    return;
                }

                let cloned;
                try {
                    cloned = JSON.parse(JSON.stringify(source));
                } catch (e) {
                    this.resolvedLayout = source;
                    return;
                }

                const cfg = cloned.config || {};
                const baseVars = cfg.dynamicVariables || {};
                const mappedVars = this.buildProductDynamicVariables();
                const activeLocale = this.getActiveLocale(cfg);
                const rootLayout = cloned.layout || {};
                const localeLayouts = rootLayout.localeLayouts;

                const localeMatch = this.resolveLocaleLayout(
                    localeLayouts,
                    activeLocale,
                    cfg.activeLocale ? String(cfg.activeLocale).toLowerCase() : 'en'
                );

                if (localeMatch && localeMatch.layout) {
                    const localeLayout = localeMatch.layout;

                    const localeDroppedItems = (Array.isArray(localeLayout.droppedItems)
                        ? localeLayout.droppedItems
                        : []
                    ).filter((item) => {
                        const category = item && item.data && item.data.category
                            ? String(item.data.category).toLowerCase()
                            : '';

                        if (category !== 'background images') return true;

                        const width = Number(item && (item.width || 0));
                        const height = Number(item && (item.height || 0));
                        return !(width === 1 && height === 1);
                    });

                    rootLayout.droppedItems = Array.isArray(localeLayout.droppedItems)
                        ? localeDroppedItems
                        : (Array.isArray(rootLayout.droppedItems) ? rootLayout.droppedItems : []);

                    if (this.hasValue(localeLayout.background)) {
                        rootLayout.background = localeLayout.background;
                    } else {
                        const backgroundItem = (rootLayout.droppedItems || []).find((item) => {
                            const category = item && item.data && item.data.category
                                ? String(item.data.category).toLowerCase()
                                : '';
                            const width = Number(item && (item.width || 0));
                            const height = Number(item && (item.height || 0));
                            return category === 'background images'
                                && item && item.data && item.data.imagePath
                                && !(width === 1 && height === 1);
                        });

                        if (backgroundItem && backgroundItem.data) {
                            rootLayout.background = {
                                id: backgroundItem.data.id,
                                name: backgroundItem.data.name || 'background',
                                imagePath: backgroundItem.data.imagePath
                            };
                        }
                    }

                    // Case 1: background is an object with imagePath — inject into droppedItems if absent
                    const bgObj = rootLayout.background;
                    if (bgObj && bgObj.imagePath) {
                        const hasBgItem = (rootLayout.droppedItems || []).some(
                            it => it.isBackgroundLayer || (it.data && String(it.data.category || '').toLowerCase() === 'background images')
                        );
                        if (!hasBgItem) {
                            const canvasW = cfg.canvasWidth || 1080;
                            const canvasH = cfg.canvasHeight || 868;
                            rootLayout.droppedItems = [
                                {
                                    id: bgObj.id || ('bg-injected-' + Date.now()),
                                    type: 'asset',
                                    isBackgroundLayer: true,
                                    position: { x: 0, y: 0 },
                                    width: canvasW,
                                    height: canvasH,
                                    data: {
                                        id: bgObj.id,
                                        name: bgObj.name || 'background',
                                        category: 'Background images',
                                        imagePath: bgObj.imagePath
                                    }
                                },
                                ...(rootLayout.droppedItems || [])
                            ];
                        }
                    }

                    if (this.hasValue(localeLayout.canvasWidth)) {
                        cfg.canvasWidth = Number(localeLayout.canvasWidth) || cfg.canvasWidth;
                    }
                    if (this.hasValue(localeLayout.canvasHeight)) {
                        cfg.canvasHeight = Number(localeLayout.canvasHeight) || cfg.canvasHeight;
                    }

                    cfg.activeLocale = localeMatch.locale;
                }

                cfg.activeState = this.deriveActiveStateFromProducts(cfg.states, cfg.activeState);

                // preserve design-time values so textStyle can compare lengths for font scaling
                cfg.designDynamicVariables = { ...baseVars };
                cfg.dynamicVariables = {
                    ...baseVars,
                    ...mappedVars
                };

                this.normalizeDroppedItemsImagePaths(rootLayout.droppedItems);

                cloned.config = cfg;
                cloned.layout = rootLayout;
                this.resolvedLayout = cloned;
            },
            loadUploadedFonts(layout) {
                try {
                    const fonts = (layout && layout.layout && layout.layout.uploadedFonts) || [];
                    if (!fonts.length) return;

                    // Prefer FontFace API when available
                    if (typeof FontFace === 'function' && document && document.fonts) {
                        fonts.forEach(f => {
                            try {
                                const family = f.family;
                                const url = f.dataUrl;
                                // load and add to document.fonts
                                const ff = new FontFace(family, `url(${url})`);
                                ff.load().then(loaded => {
                                    try { document.fonts.add(loaded); } catch (e) { /* ignore */ }
                                    }).catch(() => {
                                        // fallback to CSS injection if FontFace load fails
                                        this.injectFontCss([f]);
                                });
                            } catch (e) {
                                // fall back to CSS injection
                            }
                        });
                    } else {
                        this.injectFontCss(fonts);
                    }
                } catch (e) {
                    console.warn('loadUploadedFonts error', e);
                }
            },
            injectFontCss(fonts) {
                try {
                    const id = 'dynamic-po-template-fonts';
                    let style = document.getElementById(id);
                    if (!style) {
                        style = document.createElement('style');
                        style.id = id;
                        document.head.appendChild(style);
                    }
                    const rules = fonts.map(f => {
                        const family = f.family.replace(/'/g, "\\'");
                        const url = f.dataUrl;
                        return `@font-face { font-family: '${family}'; src: url('${url}'); font-weight: normal; font-style: normal; font-display: swap; }`;
                    }).join('\n');
                    style.innerHTML = rules;
                } catch (e) {
                    console.warn('injectFontCss error', e);
                }
            },
            assetStyle(item, index) {
                /*const pos = item.position || {};
                const w = item.width || item.originalWidth || 'auto';
                const h = item.height || item.originalHeight || 'auto';
                return {
                    position: 'absolute',
                    left: (pos.x || 0) + 'px',
                    top: (pos.y || 0) + 'px',
                    width: (typeof w === 'number' ? w + 'px' : w),
                    height: (typeof h === 'number' ? h + 'px' : h),
                    pointerEvents: 'none'
                };*/

                return this.renderer.getItemStyle(item, index)
            },
            labelStyle(item, index) {
                /*const pos = item.position || {};
                const p = item.properties || {};
                const color = p.fontColor || p.font_color || '#000000';
                const size = p.fontSize || p.font_size || 24;
                const stroke = p.stroke || 0;
                const strokeColor = p.strokeColor || p.stroke_color || '#000000';
                const family = p.fontFamily || p.font_family || 'sans-serif';
                const textShadow = stroke ? `0 ${stroke}px ${strokeColor}, 0 -${stroke}px ${strokeColor}, ${stroke}px 0 ${strokeColor}, -${stroke}px 0 ${strokeColor}` : 'none';
                return {
                    position: 'absolute',
                    left: (pos.x || 0) + 'px',
                    top: (pos.y || 0) + 'px',
                    color: color,
                    fontSize: size + 'px',
                    fontFamily: family,
                    textShadow: textShadow,
                    pointerEvents: 'none'
                };*/

                return {
                    ...this.renderer.getItemStyle(item, index),
                    ...this.renderer.getTextStyle(item)
                }
            },
            labelText(item) {
                const key = item.translationKey || (item.data && item.data.id) || '';
                const cfg = this.usedLayout && this.usedLayout.config;
                const locale = this.locale || (cfg && cfg.activeLocale) || 'en';
                const translationsByLocale = (cfg && cfg.translations) || {};
                const normalizedLocale = String(locale).toLowerCase();
                const baseLocale = normalizedLocale.includes('-') ? normalizedLocale.split('-')[0] : normalizedLocale;
                const translations = translationsByLocale[normalizedLocale]
                    || translationsByLocale[baseLocale]
                    || translationsByLocale.en
                    || {};
                let txt = translations[key] || item.data && item.data.name || key;
                
                // Get dynamic variables for replacement
                const dyn = (cfg && cfg.dynamicVariables) || {};
                const designDyn = (cfg && cfg.designDynamicVariables) || {};

                const isSinglePriceConfig = !designDyn['coins_2_value'] && !designDyn['coins_3_value'];

                //console.log("isSinglePriceConfig", isSinglePriceConfig);
                
                txt = String(txt || '');
                
                // Track coins_1_value and chips_1_value for length analysis
                txt = txt.replace(/\s*\{\{\s*([^}]+)\s*\}\}/g, (m, v) => {
                    if (dyn[v] !== undefined) {
                        // Capture coins_1_value and chips_1_value data
                        if (v === 'coins_1_value' || v === 'chips_1_value') {
                            const designValue = designDyn[v] || 'N/A';
                            const replacedValue = String(dyn[v]);
                            
                            this.textLengthData[v] = {
                                designValue: String(designValue),
                                replacedValue: replacedValue,
                                designLength: String(designValue).length,
                                replacedLength: replacedValue.length
                            };
                        }
                        return dyn[v];
                    }
                    return m;
                });
                
                // Log analysis when both values are collected (only once)
                if (isSinglePriceConfig && !this.lengthAnalyzed && this.textLengthData.coins_1_value && this.textLengthData.chips_1_value) {
                    this.lengthAnalyzed = true;
                    
                    const coins = this.textLengthData.coins_1_value;
                    const chips = this.textLengthData.chips_1_value;
                    const lengthDifference = coins.replacedLength - chips.replacedLength;
                    
                    // Store the difference for position adjustment
                    this.textLengthDifference = lengthDifference;
                    
                    /*console.log(
                        '%c[Text Length Analysis]',
                        'color: #2196F3; font-weight: bold; font-size: 13px;',
                        `\ncoins_1_value:\n  Design: ${coins.designValue} (${coins.designLength} chars)\n  Replaced: ${coins.replacedValue} (${coins.replacedLength} chars)\n\nchips_1_value:\n  Design: ${chips.designValue} (${chips.designLength} chars)\n  Replaced: ${chips.replacedValue} (${chips.replacedLength} chars)\n\nLength Difference (coins - chips): ${lengthDifference}`
                    );*/
                }
                
                return txt;
            },
            getTemplateText(item) {
                const key = item && item.translationKey;
                if (!key) return '';
                const cfg = this.usedLayout && this.usedLayout.config;
                const locale = this.locale || (cfg && cfg.activeLocale) || 'en';
                const translationsByLocale = (cfg && cfg.translations) || {};
                const normalizedLocale = String(locale).toLowerCase();
                const baseLocale = normalizedLocale.includes('-') ? normalizedLocale.split('-')[0] : normalizedLocale;
                const translations = translationsByLocale[normalizedLocale]
                    || translationsByLocale[baseLocale]
                    || translationsByLocale.en
                    || {};
                return translations[key] || '';
            },
            isButtonAsset(item) {
                const category = item && item.data && item.data.category ? String(item.data.category).toLowerCase() : '';
                const name = item && item.data && item.data.name ? String(item.data.name).toLowerCase() : '';
                return category === 'buttons' || name.indexOf('btn') !== -1;
            },
            isPriceLabelItem(item) {
                const id = item && item.data && item.data.id ? String(item.data.id).toLowerCase() : '';
                const name = item && item.data && item.data.name ? String(item.data.name).toLowerCase() : '';
                const tKey = item && item.translationKey ? String(item.translationKey).toLowerCase() : '';
                const tText = String(this.getTemplateText(item) || '').toLowerCase();

                if (/^price_\d+$/i.test(id)) return true;
                if (/^price_\d+_label$/i.test(name)) return true;
                if (/price_\d+/i.test(tKey)) return true;
                if (/buy_price_\d+/i.test(tText)) return true;
                return false;
            },
            getButtonOrderIndex(targetItem) {
                const products = this.getProducts();
                if (!products.length) return -1;

                const buttonItems = (this.items || [])
                    .filter((it) => this.isButtonAsset(it))
                    .slice()
                    .sort((a, b) => {
                        const ax = Number(a && a.position && a.position.x) || 0;
                        const bx = Number(b && b.position && b.position.x) || 0;
                        if (ax !== bx) return ax - bx;

                        const ay = Number(a && a.position && a.position.y) || 0;
                        const by = Number(b && b.position && b.position.y) || 0;
                        return ay - by;
                    });

                const idx = buttonItems.findIndex((it) => it && targetItem && it.id === targetItem.id);
                if (idx < 0 || idx >= products.length) return -1;
                return idx;
            },
            extractProductIndex(item) {
                const rawSources = [
                    item && item.data && item.data.id,
                    item && item.data && item.data.name,
                    item && item.translationKey,
                    this.getTemplateText(item)
                ];

                const patterns = [
                    /btn_buy_(\d+)/i,
                    /buy_price_(\d+)/i,
                    /price_(\d+)/i,
                    /(coins|chips|bp|lucky_coins)_(\d+)/i
                ];

                for (let i = 0; i < rawSources.length; i++) {
                    const src = rawSources[i];
                    if (!src) continue;

                    for (let j = 0; j < patterns.length; j++) {
                        const m = String(src).match(patterns[j]);
                        if (!m) continue;
                        const idx = Number(m[m.length - 1]) - 1;
                        if (Number.isFinite(idx) && idx >= 0) return idx;
                    }
                }

                const name = item && item.data && item.data.name ? String(item.data.name).toLowerCase() : '';
                if (this.isButtonAsset(item)) {
                    const direct = name.match(/btn[_\s-]*buy[_\s-]*(\d+)/i) || name.match(/btn[_\s-]*(\d+)/i) || name.match(/(\d+)/);
                    if (direct) {
                        const directIdx = Number(direct[1]) - 1;
                        if (Number.isFinite(directIdx) && directIdx >= 0) return directIdx;
                    }

                    const products = this.getProducts();
                    if (products.length === 1) return 0;

                    const orderedIdx = this.getButtonOrderIndex(item);
                    if (orderedIdx >= 0) return orderedIdx;
                }

                return -1;
            },
            isBuyItem(item) {
                const name = item && item.data && item.data.name ? String(item.data.name).toLowerCase() : '';
                const templateText = this.getTemplateText(item);
                if (this.isButtonAsset(item)) return true;
                if (this.isPriceLabelItem(item)) return true;
                if (name.indexOf('btn_buy_') !== -1) return true;
                if (/buy_price_\d+/i.test(templateText)) return true;
                return this.extractProductIndex(item) !== -1 && /buy/i.test(name);
            },
            onItemClick(item) {
                if (!this.isBuyItem(item)) return;

                const pIndex = this.extractProductIndex(item);
                if (pIndex < 0) return;

                const products = this.getProducts();
                const prod = products[pIndex];
                if (!prod) return;

                this.buy(pIndex, prod, prod.hash, prod.currency, prod.price);
            },
            buyFreeProductOnLoad() {
                if (this.loadPurchaseChecked) return;

                const products = this.getProducts();
                if (!Array.isArray(products) || !products.length || !this.items.length) return;

                for (let i = 0; i < products.length; i++) {
                    const product = products[i];
                    //if (!product || !((!product.purchased || !product.isLocked) && product.isFree === true)) continue;

                    if(product && product.isFree && !product.isLocked && !product.purchased){
                        const buyItem = this.items.find((item) => {
                            return this.isBuyItem(item) && this.extractProductIndex(item) === i;
                        });

                        if (buyItem) {
                            this.loadPurchaseChecked = true;
                            this.onItemClick(buyItem);
                            return;
                        }
                    }
                }
            },
            handleRegularReward(status) {
                this.clearRegularRewardTimer();
                if (status === 'NOT_CHECKED') {
                    this.regularRewardTimer = setInterval(() => {
                        if (this.regularReward === 'NOT_CHECKED') return;

                        const shouldAutoClaimFree = this.regularReward === 'COLLECTED';
                        this.clearRegularRewardTimer();
                        if (!shouldAutoClaimFree) return;

                        this.buyFreeProductOnLoad();
                    }, 500);
                }
            },
            clearRegularRewardTimer() {
                if (this.regularRewardTimer) {
                    clearInterval(this.regularRewardTimer);
                    this.regularRewardTimer = null;
                }
            },
            buy(pIndex, prod, hash, currency, price) {
                if (!prod || this.cooldown > 0) return;
                if (prod.isFree) {
                    // restrict repeated free claims
                    this.cooldown = 3000;
                } else {
                    // debounce
                    setTimeout(() => {
                        this.resetButton();
                    }, this.cooldown);
                }
                if (prod.isLocked || prod.purchased) return;

                this.$emit('buy', {
                    hash: hash,
                    currency: currency,
                    price: price,
                    free: !!prod.isFree,
                    poIndex: this.poIndex,
                    pIndex: pIndex
                });
            },
            unlock() {
                this.resetButton();
                const products = this.getProducts();
                for (let i = 0; i < products.length; i++) {
                    //if (!products[i].purchased || !products[i].isLocked) 
                    if (!products[i].purchased){
                        products[i].isLocked = true;
                        products[i].purchased = true;

                        if (i === products.length - 1) {
                            if (this.purchaseLimitState > 0) this.purchaseLimitState--;
                            if (this.purchaseLimitState > 0 || this.purchaseLimitState === -1) this.resetPurchase();
                        } else if (products[i + 1] && products[i + 1].isLocked) {
                            products[i + 1].isLocked = false;
                            //products[i + 1].purchased = false;
                        }
                        break;
                    }
                }

                this.refreshResolvedLayout();
            },
            resetButton() {
                this.cooldown = 0;
            },
            resetPurchase() {
                const products = this.getProducts();
                for (let i = 0; i < products.length; i++) {
                    products[i].isLocked = i > 0;
                    products[i].purchased = false;
                }

                this.refreshResolvedLayout();
            },
            getAdjustedCoinsLeft() {
                if (!this.textLengthDifference) return null;

                const coinsItem = (this.items || []).find((it) => {
                    if (!it || it.type === 'asset') return false;
                    return /\{\{\s*coins_1_value\s*\}\}/.test(this.getTemplateText(it));
                });

                if (!coinsItem) return null;

                const coinsIndex = (this.items || []).findIndex((it) => it && it.id === coinsItem.id);
                const coinsStyle = this.renderer.getItemStyle(coinsItem, coinsIndex >= 0 ? coinsIndex : 0);
                const baseLeft = parseFloat(coinsStyle.left);
                if (!Number.isFinite(baseLeft)) return null;

                const adjustment = 150 - (this.textLengthDifference * 10);
                return baseLeft + adjustment;
            },
            itemStyle(item, index) {
                const style = { ...this.renderer.getItemStyle(item, index) };
                const templateText = this.getTemplateText(item);
                const varMatch = templateText.match(/\{\{\s*([^}]+)\s*\}\}/);
                const varName = varMatch ? varMatch[1].trim() : '';
                
                // Apply length difference adjustment using renderer
                this.renderer.applyTextLengthDifferenceAdjustment(style, { 
                    varName: varName, 
                    lengthDifference: this.textLengthDifference 
                });

                const isCoinsIcon = item && item.type === 'asset' && item.data && String(item.data.name || '').toLowerCase().includes('web_po_icon_con');
                if (isCoinsIcon) {
                    const adjustedCoinsLeft = this.getAdjustedCoinsLeft();
                    if (adjustedCoinsLeft !== null) {
                        style.left = (adjustedCoinsLeft - 100) + 'px';
                    }
                }
                
                if (this.isBuyItem(item) && item.type !== 'asset') {
                    const resolvedText = this.labelText(item);
                    const cfg = this.usedLayout && this.usedLayout.config;
                    const designVars = (cfg && cfg.designDynamicVariables) || {};
                    const actualVars = (cfg && cfg.dynamicVariables) || {};
                    const dynamicValue = varName ? String(actualVars[varName] != null ? actualVars[varName] : '') : '';
                    const designValue = varName ? String(designVars[varName] != null ? designVars[varName] : '') : '';
                    this.renderer.applyDynamicPositionAdjustments(style, { resolvedText, templateText, dynamicValue, designValue });
                }
                return style;
            },
            textStyle(item){
                const style = {
                    ...this.renderer.getTextStyle(item)
                };

                const text = this.labelText(item);

                if (text.includes('\n')) {
                    style.whiteSpace = 'pre-wrap';
                }

                if (this.isBuyItem(item) && item.type !== 'asset') {
                    const cfg = this.usedLayout && this.usedLayout.config;
                    const designVars = (cfg && cfg.designDynamicVariables) || {};
                    const actualVars = (cfg && cfg.dynamicVariables) || {};
                    const key = item.translationKey || (item.data && item.data.id) || '';
                    const normalizedLocale = String(this.locale || (cfg && cfg.activeLocale) || 'en').toLowerCase();
                    const baseLocale = normalizedLocale.includes('-') ? normalizedLocale.split('-')[0] : normalizedLocale;
                    const translationsByLocale = (cfg && cfg.translations) || {};
                    const translations = translationsByLocale[normalizedLocale]
                        || translationsByLocale[baseLocale]
                        || translationsByLocale.en
                        || {};
                    const rawText = String(translations[key] || (item.data && item.data.name) || key || '');
                    this.renderer.applyDynamicTextAdjustments(style, {
                        designVars,
                        actualVars,
                        rawText,
                        isBuyItem: true
                    });
                }

                return style;
            }
        }
    };
</script>

<style scoped>
    .po-canvas-host { margin: 0 auto; }
    .po-canvas { background: transparent; }
    .po-bg { position: absolute; left: 0; top: 0; width: 100%; height: 100%; pointer-events: none }
    .po-asset { position: absolute; }
    .po-item{
    position:absolute;
    display:flex;
    flex-direction:column;
    align-items:center;
}

.po-label{
    display:block;
}

.po-label-text{
    display:inline-block;
}

.canvas-items{
    position:absolute;
    inset:0;
    z-index:3;
    pointer-events:none;
}

.canvas-item{
    position:absolute;
    pointer-events:auto;
    box-sizing:border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.canvas-item.is-buy-item{
    cursor:pointer;
}

.canvas-item.is-buy-item *{
    cursor: inherit;
}

.asset-image{
    display:block;
    width:100%;
    height:100%;
    object-fit:fill;
}

.item-label{
    display:block;
    white-space:nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/DynamicPoTemplate.vue